// exchangeRuleHelpers — normalize inbox-rule details out of an Exchange audit
// raw event into a flat [{Name, Value}] list the "Rule / Operation Parameters"
// panel already knows how to render.
//
// Two distinct Exchange audit schemas carry inbox-rule changes:
//   1. New-InboxRule / Set-InboxRule / Remove-InboxRule (cmdlet / admin audit) —
//      details live in a flat `Parameters` array of {Name, Value} pairs
//      (ForwardTo, DeleteMessage, ...). The portal already rendered these.
//   2. UpdateInboxRules (mailbox audit — a user editing rules in Outlook / OWA) —
//      the rule lives in `OperationProperties` instead (RuleOperation, RuleName,
//      RuleState, RuleCondition, RuleProvider, RuleActions), with RuleActions a
//      nested JSON string. `Parameters` is null on these events, so the panel
//      showed nothing and the forward-to target was hidden in the raw JSON.
//
// This returns a normalized list covering BOTH schemas (with RuleActions decoded
// to a readable "Forward → recipients" summary), or null when the event carries
// no inbox-rule detail — so unrelated Exchange operations don't render stray
// OperationProperties rows.

// OperationProperties keys we surface, in display order. Everything else
// (MailAccessType, internal flags, etc.) is intentionally dropped.
const RULE_PROP_ORDER = ['RuleOperation', 'RuleName', 'RuleState', 'RuleProvider', 'RuleCondition', 'RuleActions', 'RuleId'];

// Friendly labels for the internal RuleOperation verbs.
const RULE_OP_LABELS = {
  AddMailboxRule: 'Add rule',
  ModifyMailboxRule: 'Modify rule',
  RemoveMailboxRule: 'Remove rule',
};

// RuleActions is a JSON string like:
//   [{"ActionType":"Forward","Recipients":["ap@x.com"],"ForwardFlags":"None"}]
// Decode it into a human-readable one-liner. Falls back to the raw string when
// it isn't the expected shape so nothing is silently lost.
function decodeRuleActions(value) {
  let actions = value;
  if (typeof value === 'string') {
    try { actions = JSON.parse(value); } catch { return value; }
  }
  if (!Array.isArray(actions)) return value;
  const parts = actions.map((a) => {
    if (!a || typeof a !== 'object') return String(a);
    const type = a.ActionType || a.Type || 'Action';
    const recips = Array.isArray(a.Recipients) ? a.Recipients.join(', ')
      : (a.Recipients || a.ForwardTo || a.RedirectTo || null);
    const folder = a.FolderName || a.MoveToFolder || a.Folder || null;
    if (/forward/i.test(type) && recips) return `Forward → ${recips}`;
    if (/redirect/i.test(type) && recips) return `Redirect → ${recips}`;
    if (/move/i.test(type) && folder) return `Move to folder: ${folder}`;
    if (/delete/i.test(type)) return 'Delete message';
    if (/markasread/i.test(type)) return 'Mark as read';
    if (recips) return `${type} → ${recips}`;
    if (folder) return `${type}: ${folder}`;
    return type;
  });
  return parts.filter(Boolean).join('   •   ') || value;
}

// Returns [{Name, Value}] for the panel, or null when there's nothing to show.
export function parseInboxRuleParameters(rawEvent) {
  if (!rawEvent || typeof rawEvent !== 'object') return null;

  // 1) Cmdlet schema — flat Parameters array (New-/Set-/Remove-InboxRule and
  //    other Set- ops). Preserve the pre-existing behavior verbatim.
  let params = rawEvent.Parameters;
  if (typeof params === 'string') {
    try { params = JSON.parse(params); } catch { params = null; }
  }
  if (Array.isArray(params) && params.length > 0) return params;

  // 2) Mailbox-audit schema — OperationProperties (UpdateInboxRules).
  let opProps = rawEvent.OperationProperties;
  if (typeof opProps === 'string') {
    try { opProps = JSON.parse(opProps); } catch { opProps = null; }
  }
  if (!Array.isArray(opProps) || opProps.length === 0) return null;

  // Only treat this as an inbox-rule event when rule-shaped keys are present, so
  // OperationProperties for unrelated Exchange operations isn't surfaced here.
  const hasRuleKeys = opProps.some((p) => p && typeof p.Name === 'string' && /^Rule/.test(p.Name));
  if (!hasRuleKeys) return null;

  const byName = {};
  for (const p of opProps) {
    if (p && typeof p.Name === 'string') byName[p.Name] = p.Value;
  }

  const rows = [];
  for (const key of RULE_PROP_ORDER) {
    if (!(key in byName)) continue;
    let val = byName[key];
    // RuleName often has no Value (unnamed rule) — skip empty rows.
    if (val === undefined || val === null || val === '') continue;
    if (key === 'RuleOperation') val = RULE_OP_LABELS[val] || val;
    else if (key === 'RuleActions') val = decodeRuleActions(val);
    rows.push({ Name: key, Value: String(val) });
  }
  return rows.length > 0 ? rows : null;
}
